import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Container, Typography, styled } from '@mui/material';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { FormChanges } from 'utils';
import { Formik, Field } from 'formik';
import { vehicleActions } from 'state/modules/vehicles';
import { MaterialSelectField, DateTimePickerField, SwitchField } from 'components/FormFields';
import PropTypes from 'prop-types';

// Styled Components
const StyledRequirementList = styled('ul')({
  marginTop: 15,
});

const StyledRequirementItem = styled('li')({
  // Using default list item styles
});

const VehicleActivation = ({ currentVehicle, formRef, displaySuccessModal, onSave }) => {
  const dispatch = useDispatch();

  const effectiveOptions = [
    {
      value: 'immediately',
      label: i18n.t('vehicle.immediately'),
    },
    {
      value: 'date',
      label: i18n.t('vehicle.specific_date_time'),
    },
  ];

  useEffect(() => {
    if (currentVehicle?.id) {
      dispatch(vehicleActions.fetchVehicleById(currentVehicle.id));
    }
  }, []); // eslint-disable-line

  const initialValues = {
    activation: true,
    effectiveOption: 'immediately',
    start_time: '',
  };

  const formSchema = Yup.object().shape({
    activation: Yup.boolean(),
    effectiveOption: Yup.string(),
    start_time: Yup.string(),
  });

  const onSubmit = (values) => {
    if (!values.activation) return displaySuccessModal();

    let payload = {
      status: 'ACTIVE',
    };

    if (values.start_time) payload.start_time = values.start_time;

    dispatch(vehicleActions.changeVehicleStatus(payload));
  };

  return (
    <Box>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={{ xs: 10 }}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">
                        <Box sx={{ fontWeight: 'fontWeightBold', fontSize: 14, height: '100%' }}>
                          {i18n.t('vehicle.vehicle_ativation_title')}
                        </Box>
                      </Typography>
                      <Box sx={{ mx: 3 }}>
                        <Field name="activation" component={SwitchField} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'left', justifyContent: 'flex-start' }}>
                      <Typography variant="h4">
                        <Box sx={{ fontSize: 12, height: '100%', maxWidth: '500px' }}>
                          {i18n.t('vehicle.vehicle_ativation_description')}
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                  {values.activation && (
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <Grid size={{ xs: 12 }} sx={{ mt: 1.875 }}>
                          <Field
                            name="effectiveOption"
                            component={MaterialSelectField}
                            options={effectiveOptions?.map((val) => {
                              return {
                                value: val.value,
                                label: val.label,
                              };
                            })}
                            placeholder={'Effective'}
                          />
                        </Grid>
                      </Box>
                      {values.effectiveOption === 'date' && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            mt: 2,
                          }}
                        >
                          <Grid size={{ xs: 12 }} sx={{ mt: 1.875 }}>
                            <Field
                              name="start_time"
                              component={DateTimePickerField}
                              label={'Start date and time'}
                              editable={true}
                            />
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }}>
                    {Array.isArray(currentVehicle?.activation_requirements) && (
                      <Typography variant="h6">
                        <Box sx={{ fontWeight: 'fontWeightBold', fontSize: 14, mt: 3 }}>
                          {i18n.t('vehicle.missing')}
                        </Box>
                      </Typography>
                    )}
                    {Array.isArray(currentVehicle?.activation_requirements) && (
                      <StyledRequirementList>
                        {currentVehicle.activation_requirements.map((requiremment, index) => (
                          <StyledRequirementItem key={index}>{requiremment}</StyledRequirementItem>
                        ))}
                      </StyledRequirementList>
                    )}
                  </Grid>
                  <FormChanges initialValues={initialValues} onSave={onSave} />
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

VehicleActivation.propTypes = {
  currentVehicle: PropTypes.object,
  formRef: PropTypes.object,
  displaySuccessModal: PropTypes.func,
  onSave: PropTypes.func,
};

export default VehicleActivation;
